/**
 * Scroll-triggered count-up animations.
 *
 * Pair with a screen-reader-only sibling:
 * <span class="visually-hidden" data-countup-sr>$8,411,760 invested</span>
 * <span data-countup data-countup-value="8411760" ...></span>
 *
 * While animating, the visual span is aria-hidden; AT reads data-countup-sr.
 * When complete (or reduced motion / disabled), sr-only is hidden to avoid duplicates.
 */
const CountUp = {
  observed: new WeakSet(),

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  parseOptions(el) {
    const { dataset } = el;

    return {
      enabled: dataset.countupEnabled !== 'false',
      value: parseFloat(dataset.countupValue || '0') || 0,
      positions: parseInt(dataset.countupPositions || '0', 10) || 0,
      prefix: dataset.countupPrefix || '',
      suffix: dataset.countupSuffix || '',
      format: dataset.countupFormat || 'plain',
      duration: parseInt(dataset.countupDuration || '1400', 10),
      threshold: parseFloat(dataset.countupThreshold || '0.15'),
      rootMargin: dataset.countupRootMargin || '0px',
    };
  },

  getStartValue(value, positions) {
    if (!positions || positions <= 0) return 0;

    const divisor = 10 ** positions;
    return Math.floor(value / divisor) * divisor;
  },

  formatValue(value, opts) {
    const rounded = Math.round(value);

    if (opts.format === 'currency') {
      const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(rounded);
      return `${opts.prefix}${formatted}`;
    }

    if (opts.format === 'integer') {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(rounded);
    }

    return `${rounded}${opts.suffix}`;
  },

  getSrElement(el) {
    const parent = el.parentElement;
    if (!parent) return null;
    return parent.querySelector('[data-countup-sr]');
  },

  setScreenReaderMode(el, useSrOnly) {
    const sr = this.getSrElement(el);

    if (useSrOnly) {
      el.setAttribute('aria-hidden', 'true');
      sr?.removeAttribute('aria-hidden');
      return;
    }

    el.removeAttribute('aria-hidden');
    sr?.setAttribute('aria-hidden', 'true');
  },

  setFinal(el, opts) {
    el.textContent = this.formatValue(opts.value, opts);
    this.setScreenReaderMode(el, false);
  },

  animate(el, opts) {
    this.setScreenReaderMode(el, true);

    const start = opts.start;
    const target = opts.value;
    const duration = opts.duration;
    let startTime = null;

    const tick = (now) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      const current = start + (target - start) * eased;

      el.textContent = this.formatValue(current, opts);

      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }

      this.setFinal(el, opts);
    };

    requestAnimationFrame(tick);
  },

  initElement(el) {
    if (this.observed.has(el)) return;

    const opts = this.parseOptions(el);
    opts.start = this.getStartValue(opts.value, opts.positions);

    if (!opts.enabled || opts.value <= 0) {
      this.setFinal(el, opts);
      return;
    }

    this.observed.add(el);

    if (this.prefersReducedMotion()) {
      this.setFinal(el, opts);
      return;
    }

    el.textContent = this.formatValue(opts.start, opts);
    this.setScreenReaderMode(el, true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          this.animate(entry.target, opts);
        });
      },
      { threshold: opts.threshold, rootMargin: opts.rootMargin }
    );

    observer.observe(el);
  },

  init(root = document) {
    root.querySelectorAll('[data-countup]').forEach((el) => this.initElement(el));
  },
};

document.addEventListener('DOMContentLoaded', () => CountUp.init());
document.addEventListener('shopify:section:load', (event) => CountUp.init(event.target));
